import { useState } from 'react'
import { Dialog } from './Dialog'
import { CROP_VIEW, CropCanvas, CropZoom, type CropCopy } from './PhotoCropDialog'
import {
  clampCropIn,
  cropBoxIn,
  cropFromNormIn,
  CROP_SHAPES,
  frameFor,
  initialCropIn,
  normRectOf,
  shapeAspect,
  type Crop,
  type CropShapeKey,
  type NormRect,
} from '../lib/photoCrop'

/**
 * Crop a feed photo before it is posted (FED-66, David 12 Oct).
 *
 * The card photo's widget (CropCanvas: drag, pinch, wheel, keys, and the
 * slider) in a frame whose shape the poster picks — Original, Square, Tall
 * 4:5, Wide 16:9. Fixed shapes rather than free corners, for a phone.
 *
 * ── WHAT IT RETURNS IS FRACTIONS ────────────────────────────────────────────
 *
 * `onUse(rect, shape)` hands back the crop as fractions of the whole photo,
 * never pixels: the composer cuts the feed copy AND the full-size copy from
 * the same fractions (lib/photos.ts cropPhoto / prepareOriginal), so tags and
 * Gallery downloads agree with what was posted. See lib/photoCrop.ts.
 *
 * ── NEW PHOTOS ONLY ─────────────────────────────────────────────────────────
 *
 * The composer offers this on a photo that has not been uploaded. A posted
 * photo's file is what its permissions, tags, stars, contest entries and
 * downloads are keyed by (141's storage path), so re-cropping one would mean a
 * new file and moving all of that across. Remove it and add it again instead.
 *
 * Rendered INSIDE the composer's dialog, like the tag editor: 161 made Dialog
 * trap Tab only when topmost, so a nested dialog is safe here (the card's
 * crop predates that and is a sibling for the reason in its header).
 */
export type FeedCropCopy = {
  dialog: CropCopy
  shape: string
  shapes: Record<CropShapeKey, string>
  tagsNote: string
}

export function FeedPhotoCrop({
  source,
  initialShape,
  initialRect,
  pinned,
  busy,
  error,
  onUse,
  onCancel,
  t,
}: {
  /** The whole photo, decoded; null while it decodes or when shut. */
  source: ImageBitmap | null
  initialShape: CropShapeKey
  /** The crop chosen last time, or null for the whole photo. */
  initialRect: NormRect | null
  /** Somebody is pinned on the photo, so say what a crop does to a pin. */
  pinned: boolean
  busy: boolean
  error: string | null
  onUse: (rect: NormRect, shape: CropShapeKey) => void
  onCancel: () => void
  t: FeedCropCopy
}) {
  const w = source?.width ?? 1
  const h = source?.height ?? 1

  // Kept WITH the photo it belongs to (the card dialog's reason): a new
  // photo opens on its own last crop on its very first frame.
  function opening(): { shape: CropShapeKey; crop: Crop } {
    const f = frameFor(shapeAspect(initialShape, w, h), CROP_VIEW)
    return {
      shape: initialShape,
      crop: initialRect ? cropFromNormIn(f, w, h, initialRect) : clampCropIn(f, w, h, initialCropIn(w, h)),
    }
  }
  const [held, setHeld] = useState<{ src: ImageBitmap | null; shape: CropShapeKey; crop: Crop }>({
    src: null,
    shape: 'original',
    crop: initialCropIn(1, 1),
  })
  const cur = held.src === source ? held : opening()
  const frame = frameFor(shapeAspect(cur.shape, w, h), CROP_VIEW)

  function setCrop(next: (c: Crop) => Crop) {
    setHeld((s) => {
      const base = s.src === source ? s : { src: source, ...opening() }
      return { ...base, src: source, crop: next(base.crop) }
    })
  }

  /** A new shape keeps the point you were looking at in the middle. */
  function pickShape(key: CropShapeKey) {
    setHeld((s) => {
      const base = s.src === source ? s : { src: source, ...opening() }
      const f = frameFor(shapeAspect(key, w, h), CROP_VIEW)
      return { src: source, shape: key, crop: clampCropIn(f, w, h, { zoom: 1, cx: base.crop.cx, cy: base.crop.cy }) }
    })
  }

  function use() {
    onUse(normRectOf(cropBoxIn(frame, w, h, clampCropIn(frame, w, h, cur.crop)), w, h), cur.shape)
  }

  return (
    <Dialog open={source !== null} title={t.dialog.cropTitle} onClose={busy ? () => undefined : onCancel} className="croppanel">
      <div className="cropsheet">
        <div className="cropshapes" role="group" aria-label={t.shape}>
          {CROP_SHAPES.map((s) => {
            const a = shapeAspect(s.key, w, h)
            return (
              <button
                key={s.key}
                type="button"
                className={`chip chip--sm cropshapes__btn${cur.shape === s.key ? ' chip--on' : ''}`}
                aria-pressed={cur.shape === s.key}
                disabled={busy}
                onClick={() => pickShape(s.key)}
              >
                {/* The shape itself, 14px on its long side. */}
                <span
                  className="cropshapes__glyph"
                  style={a >= 1 ? { inlineSize: 14, blockSize: Math.max(4, Math.round(14 / a)) } : { inlineSize: Math.max(4, Math.round(14 * a)), blockSize: 14 }}
                  aria-hidden="true"
                />
                {t.shapes[s.key]}
              </button>
            )
          })}
        </div>

        {source && (
          <CropCanvas
            source={source}
            frame={frame}
            crop={cur.crop}
            setCrop={setCrop}
            busy={busy}
            label={t.dialog.cropFrameLabel}
          />
        )}

        <p className="cropsheet__help">{t.dialog.cropHelp}</p>
        {pinned && <p className="cropsheet__help">{t.tagsNote}</p>}

        <CropZoom frame={frame} w={w} h={h} crop={cur.crop} setCrop={setCrop} busy={busy} label={t.dialog.zoomLabel} />

        {error && <p className="profile__error" role="alert">{error}</p>}

        <div className="cropsheet__btns">
          <button type="button" className="btn btn--primary" disabled={busy || !source} onClick={use}>
            {busy ? t.dialog.photoSaving : t.dialog.usePhoto}
          </button>
          <button type="button" className="btn btn--quiet" disabled={busy} onClick={onCancel}>
            {t.dialog.cancel}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
